/* Contact-form messages.
 *
 * These go to ONE top-level `feedback` collection, not to
 * users/{uid}/feedback where they used to live. The old shape stored each
 * message under its sender's own account, which read as tidy and was in
 * practice an inbox nobody could open: the Firestore console cannot browse
 * across a subcollection, so finding a message meant already knowing which
 * uid to look under. Messages arrived and sat there. A flat collection is
 * one console click and `node scripts/read-feedback.mjs`.
 *
 * The sender's uid rides along as a field so the messages stay the sender's
 * own data — firestore.rules lets them read and delete their own, which is
 * what account deletion needs (these documents carry an email address), and
 * it is why `feedback` is listed in userDataPaths.js#UID_QUERIED_COLLECTIONS.
 *
 * Client-writable collections are spam cannons unless the rules make them
 * otherwise, so the shape here is exactly what the rules enforce — see the
 * `match /feedback/{docId}` block for why each field is bounded.
 */
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase.js";
import { t } from "./i18n.js";

/* i18n-ignore — the STORED value, not a label. It lands in the feedback
 * collection and derives the analytics event, so it must not vary by
 * language; App.jsx translates the option each one wears. */
export const FEEDBACK_TOPICS = ["Product feedback", "Bug report", "Opening content", "Privacy request"];

export const MESSAGE_MAX = 4000;

export async function submitFeedback({ message, email, topic }) {
  const user = auth?.currentUser;
  if (!db || !user) return { error: t("feedback.signInFirst") };
  const text = message.trim();
  if (!text) return { error: t("feedback.writeSomething") };
  try {
    await addDoc(collection(db, "feedback"), {
      uid: user.uid,
      message: text.slice(0, MESSAGE_MAX),
      // The reply address the sender typed, which is NOT necessarily their
      // account address — someone signed in with Google may want replies
      // elsewhere. `accountEmail` records the one we already know, so a
      // message with no reply address is still answerable.
      email: email.trim().slice(0, 200) || null,
      accountEmail: user.email || null,
      topic: FEEDBACK_TOPICS.includes(topic) ? topic : "Product feedback", // i18n-ignore — stored value
      createdAt: Date.now(),
      // Server clock as well as the client's: `createdAt` is what the app
      // sorts by, but a wrong device clock would bury a message at the
      // bottom of the inbox forever.
      receivedAt: serverTimestamp(),
      handled: false,
    });
    return {};
  } catch (error) {
    return { error: error.code || t("feedback.sendFailed") };
  }
}
